/**
 * use-text-cylinder
 *
 * Hook para gestionar el estado y controles de un cilindro de texto rotatorio 3D.
 *
 * Features:
 * - State management: currentIndex, isAnimating
 * - Controls: next(), prev(), goTo(index), pause(), resume()
 * - Auto-rotate con interval configurable
 * - Direction-aware: calcula ruta más corta
 *
 * @example
 * ```typescript
 * const { state, controls } = useTextCylinder({
 *   items: ['Fast', 'Reliable', 'Scalable'],
 *   autoRotate: true,
 *   duration: 600
 * });
 *
 * // Controlar manualmente
 * controls.next();
 * controls.goTo(2);
 * controls.pause();
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react'

export interface TextCylinderOptions<T = React.ReactNode> {
  /** Array de items a mostrar en el cilindro */
  items: T[]
  /** Habilitar rotación automática */
  autoRotate?: boolean
  /** Intervalo de rotación automática en ms */
  autoRotateInterval?: number
  /** Duración de la animación en ms */
  duration?: number
  /** Función de easing CSS */
  easing?: string
  /** Dirección de auto-rotate: 'forward' o 'backward' */
  direction?: 'forward' | 'backward'
}

export interface TextCylinderState {
  /** Índice actual del item visible */
  currentIndex: number
  /** Si hay una animación en curso */
  isAnimating: boolean
}

export interface TextCylinderControls {
  /** Ir al siguiente item */
  next: () => void
  /** Ir al item anterior */
  prev: () => void
  /** Ir a un índice específico */
  goTo: (index: number) => void
  /** Pausar auto-rotate */
  pause: () => void
  /** Reanudar auto-rotate */
  resume: () => void
  /** Toggle pause/resume */
  toggle: () => void
}

export interface UseTextCylinderReturn {
  state: TextCylinderState
  controls: TextCylinderControls
  setIsAnimating: (isAnimating: boolean) => void
  animationRef: React.MutableRefObject<Animation | null>
}

export function useTextCylinder<T = React.ReactNode>({
  items,
  autoRotate = false,
  autoRotateInterval = 3000,
  duration = 600,
  easing = 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  direction = 'forward',
}: TextCylinderOptions<T>): UseTextCylinderReturn {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  const animationRef = useRef<Animation | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Navegar al siguiente item
  const next = useCallback(() => {
    if (items.length === 0) return
    setCurrentIndex((prev) => (prev + 1) % items.length)
  }, [items.length])

  // Navegar al item anterior
  const prev = useCallback(() => {
    if (items.length === 0) return
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length)
  }, [items.length])

  // Navegar a índice específico
  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= items.length) {
        console.warn(`[useTextCylinder] Índice ${index} fuera de rango (0-${items.length - 1})`)
        return
      }
      setCurrentIndex(index)
    },
    [items.length]
  )

  // Pausar auto-rotate
  const pause = useCallback(() => {
    setIsPaused(true)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Reanudar auto-rotate
  const resume = useCallback(() => {
    setIsPaused(false)
  }, [])

  // Toggle pause/resume
  const toggle = useCallback(() => {
    if (isPaused) {
      resume()
    } else {
      pause()
    }
  }, [isPaused, pause, resume])

  // Auto-rotate effect
  useEffect(() => {
    if (!autoRotate || isPaused || items.length <= 1) {
      return
    }

    intervalRef.current = setInterval(() => {
      if (direction === 'forward') {
        next()
      } else {
        prev()
      }
    }, autoRotateInterval)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [autoRotate, isPaused, autoRotateInterval, direction, items.length, next, prev])

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        try {
          animationRef.current.cancel()
        } catch (e) {
          // Animation already canceled
        }
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const state: TextCylinderState = {
    currentIndex,
    isAnimating,
  }

  const controls: TextCylinderControls = {
    next,
    prev,
    goTo,
    pause,
    resume,
    toggle,
  }

  return {
    state,
    controls,
    setIsAnimating,
    animationRef,
  }
}
